import { useCallback, useEffect, useRef } from 'react';
import { UploadFile, FileUploaderAdapter, UploadMode } from '../types';
import { mergeUploadResponse } from '../utils/normalizeFile';

type UseUploadQueueParams = {
  files: UploadFile[];
  onChange: (files: UploadFile[]) => void;
  adapter: FileUploaderAdapter;
  uploadMode: UploadMode;
};

type UseUploadQueueReturn = {
  uploadAll: () => Promise<UploadFile[]>;
  retryFile: (key: string) => void;
};

/**
 * Manages the upload queue for FileUploader.
 *
 * In 'auto' mode, any file that enters with status='idle' is uploaded immediately.
 * In 'manual' mode, uploads only start when uploadAll() is called externally via ref.
 *
 * Uses a stable ref to always access the latest files/onChange without stale closures.
 */
export function useUploadQueue({
  files,
  onChange,
  adapter,
  uploadMode,
}: UseUploadQueueParams): UseUploadQueueReturn {
  // Keep a stable ref so callbacks inside uploadSingle don't close over stale state
  const stateRef = useRef({ files, onChange });
  useEffect(() => {
    stateRef.current = { files, onChange };
  });

  // Track which keys are currently being uploaded to avoid double-starting
  const uploadingKeys = useRef<Set<string>>(new Set());

  const uploadSingle = useCallback(
    async (key: string, rawFile?: File): Promise<UploadFile> => {
      if (uploadingKeys.current.has(key)) {
        // Already in flight — return current state
        return stateRef.current.files.find((f) => f._key === key)!;
      }

      uploadingKeys.current.add(key);

      // Mark as uploading
      stateRef.current.onChange(
        stateRef.current.files.map((f) =>
          f._key === key ? { ...f, status: 'uploading', progress: 0 } : f
        )
      );

      const fileRecord = stateRef.current.files.find((f) => f._key === key);
      if (!fileRecord) {
        uploadingKeys.current.delete(key);
        throw new Error(`File with key "${key}" not found.`);
      }

      // We need the actual File object. It may be passed directly (from retryFile workaround)
      // or obtained from the adapter. Since UploadFile doesn't store the raw File,
      // the adapter is responsible for looking up or receiving it.
      // Here we pass an empty File placeholder — consumers provide their adapter
      // with access to the raw file via closure if needed.
      // The rawFile param covers the internal path where we do have the File.
      const file = rawFile ?? new File([], fileRecord.name, { type: fileRecord.mimeType });

      try {
        const response = await adapter.uploadFile(file, (progress) => {
          stateRef.current.onChange(
            stateRef.current.files.map((f) =>
              f._key === key ? { ...f, progress } : f
            )
          );
        });

        const updated = mergeUploadResponse(
          { ...fileRecord, status: 'uploaded', progress: 100 },
          response
        );

        stateRef.current.onChange(
          stateRef.current.files.map((f) => (f._key === key ? updated : f))
        );

        uploadingKeys.current.delete(key);
        return updated;
      } catch {
        const failed: UploadFile = { ...fileRecord, status: 'error', progress: 0 };
        stateRef.current.onChange(
          stateRef.current.files.map((f) => (f._key === key ? failed : f))
        );
        uploadingKeys.current.delete(key);
        return failed;
      }
    },
    [adapter]
  );

  // Raw-file registry: stores the raw File object keyed by _key so retry can re-use it
  const rawFileRegistry = useRef<Map<string, File>>(new Map());

  /**
   * Register a raw File under its _key. Called by FileUploader when files are added.
   * Exposed via the returned object so FileUploader can call it.
   */
  const registerRawFile = useCallback((key: string, file: File) => {
    rawFileRegistry.current.set(key, file);
  }, []);

  // Auto-upload: watch for new idle files
  useEffect(() => {
    if (uploadMode !== 'auto') return;

    const idleFiles = files.filter(
      (f) => f.status === 'idle' && !uploadingKeys.current.has(f._key)
    );

    for (const f of idleFiles) {
      const raw = rawFileRegistry.current.get(f._key);
      uploadSingle(f._key, raw);
    }
  }, [files, uploadMode, uploadSingle]);

  const uploadAll = useCallback(async (): Promise<UploadFile[]> => {
    const idleFiles = stateRef.current.files.filter((f) => f.status === 'idle');

    const results = await Promise.allSettled(
      idleFiles.map((f) => {
        const raw = rawFileRegistry.current.get(f._key);
        return uploadSingle(f._key, raw);
      })
    );

    return results.map((r) =>
      r.status === 'fulfilled'
        ? r.value
        : ({ status: 'error', progress: 0 } as UploadFile)
    );
  }, [uploadSingle]);

  const retryFile = useCallback(
    (key: string) => {
      // Reset status to idle
      stateRef.current.onChange(
        stateRef.current.files.map((f) =>
          f._key === key ? { ...f, status: 'idle', progress: 0 } : f
        )
      );

      if (uploadMode === 'auto') {
        // Kick off immediately; status will be 'idle' on next render which triggers the effect.
        // We also start explicitly to avoid a one-render delay.
        const raw = rawFileRegistry.current.get(key);
        setTimeout(() => uploadSingle(key, raw), 0);
      }
    },
    [uploadMode, uploadSingle]
  );

  // Expose registerRawFile on the returned object so FileUploader can call it
  return Object.assign({ uploadAll, retryFile }, { registerRawFile });
}
